import { Button, FormHelperText } from '@mui/material';
import ButtonGroup from 'components/ButtonGroup';
import DropzoneComponent, { UploadedFile } from 'components/Dropzone';
import FieldRow from 'components/FormElements/FieldRow';
import RecordView from 'components/RecordView';
import { UploadType } from 'further-types/files';
import { FileType } from 'further-ui/utils';
import useFileUpload from 'hooks/ui/useFileUpload';
import { useNotification } from 'hooks/ui/useNotification';
import { Control, Controller } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import type { TrancheFormInput } from '../useTrancheForm';

const acceptedFundDocumentFileTypes: Record<string, Array<string>> = {
  [FileType.DOCX]: ['.docx'],
  [FileType.PDF]: ['.pdf'],
  [FileType.JPGImage]: ['.jpg', '.jpeg'],
  [FileType.PNGImage]: ['.png'],
};

const useStyles = makeStyles()(() => ({
  marginLeftBtn: {
    marginLeft: 10,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

type Props = {
  control: Control<TrancheFormInput>;
  handleCancel: () => void;
  handleClickTranchePromotion: () => void;
  id: string;
  disabled: boolean;
  usesDealDocumentation: boolean;
  isLP: boolean;
  viewOnly: boolean;
  additionalDocuments: TrancheFormInput['additionalDocuments'];
  onChangeAdditionalDocuments: (
    value: TrancheFormInput['additionalDocuments'],
  ) => void;
};

export default function TranchePromotionInformation({
  control,
  handleCancel,
  handleClickTranchePromotion,
  id,
  disabled = false,
  usesDealDocumentation = false,
  isLP = false,
  viewOnly = false,
  additionalDocuments,
  onChangeAdditionalDocuments,
}: Props) {
  const { classes } = useStyles();
  const { uploadFile } = useFileUpload();
  const { error: showError } = useNotification();

  const makeOnDropFile =
    (onChange: (value: TrancheFormInput['applicationFormFile']) => void) =>
    async (files: Array<UploadedFile>) => {
      if (!files[0]) return;
      const { data, error } = await uploadFile(
        files[0],
        UploadType.FundDocuments,
      );
      if (data) {
        onChange({
          fileName: files[0].name,
          filePath: data.filePath,
          previewUri: data.url,
        });
      } else {
        showError(error.message);
      }
    };

  const handleDropFiles = async (files: Array<UploadedFile>) => {
    if (!files[0]) return;
    const results = await Promise.all(
      files.map((file) => uploadFile(file, UploadType.FundDocuments)),
    );
    const uploaded: TrancheFormInput['additionalDocuments'] = [];
    for (let i = 0; i < results.length; i++) {
      const { data, error } = results[i];
      if (data) {
        uploaded.push({
          fileName: files[i].name,
          filePath: data.filePath,
          previewUri: data.url,
        });
      } else {
        showError(error.message);
        return;
      }
    }
    onChangeAdditionalDocuments([...additionalDocuments, ...uploaded]);
  };

  return (
    <RecordView>
      {usesDealDocumentation ? (
        <FieldRow
          title="Deal documentation"
          tooltipText="Please upload all documentation relating to this opportunity that you want investors to acknowledge or agree to."
        >
          <DropzoneComponent
            name="dealDocumentation"
            accept={acceptedFundDocumentFileTypes}
            onDropFile={handleDropFiles}
            onRemove={(index) =>
              onChangeAdditionalDocuments(
                additionalDocuments.filter((_, i) => i !== index),
              )
            }
            maxFiles={10}
            disabled={viewOnly}
            files={additionalDocuments.map((file) => ({
              fileName: file.fileName,
              preview: file.previewUri ?? '',
            }))}
          />
        </FieldRow>
      ) : (
        <FieldRow
          title="Legal agreement*"
          tooltipText="Please upload your subscription management agreement, limited partnership agreement or, if you have a prospectus, your application form."
        >
          <Controller
            name="applicationFormFile"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <DropzoneComponent
                  name="applicationFormFiles"
                  accept={acceptedFundDocumentFileTypes}
                  onDropFile={makeOnDropFile(field.onChange)}
                  disabled={viewOnly}
                  files={
                    field.value
                      ? [
                          {
                            fileName: field.value.fileName,
                            preview: field.value.previewUri ?? '',
                          },
                        ]
                      : []
                  }
                  onRemove={() => field.onChange(null)}
                />
                {fieldState.error && (
                  <FormHelperText error>
                    {fieldState.error.message}
                  </FormHelperText>
                )}
              </>
            )}
          />
        </FieldRow>
      )}

      {isLP ? (
        <FieldRow
          title="Additional documents"
          tooltipText="Upload any other fund documentation here, for example your PPM, fundraising presentation, FAQ document, KID or other documents you wish to share."
        >
          <DropzoneComponent
            name="additionalDocuments"
            accept={acceptedFundDocumentFileTypes}
            onDropFile={handleDropFiles}
            onRemove={(index) =>
              onChangeAdditionalDocuments(
                additionalDocuments.filter((_, i) => i !== index),
              )
            }
            maxFiles={10}
            disabled={viewOnly}
            files={additionalDocuments.map((file) => ({
              fileName: file.fileName,
              preview: file.previewUri ?? '',
            }))}
          />
        </FieldRow>
      ) : (
        !usesDealDocumentation && (
          <>
            <FieldRow
              title="Offering document"
              tooltipText="Please upload your core fund marketing document. This may be a prospectus, information memorandum or key fundraising presentation."
            >
              <Controller
                name="prospectusFile"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <DropzoneComponent
                      name="prospectusFiles"
                      accept={acceptedFundDocumentFileTypes}
                      onDropFile={makeOnDropFile(field.onChange)}
                      disabled={viewOnly}
                      files={
                        field.value
                          ? [
                              {
                                fileName: field.value.fileName,
                                preview: field.value.previewUri ?? '',
                              },
                            ]
                          : []
                      }
                      onRemove={() => field.onChange(null)}
                    />
                    {fieldState.error && (
                      <FormHelperText error>
                        {fieldState.error.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </FieldRow>
            <FieldRow
              title="Key information document"
              tooltipText="Please upload your Key Information Document (KID) if you have been required to produce one."
            >
              <Controller
                name="keyInfoFile"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <DropzoneComponent
                      name="keyInfoFiles"
                      accept={acceptedFundDocumentFileTypes}
                      onDropFile={makeOnDropFile(field.onChange)}
                      disabled={viewOnly}
                      files={
                        field.value
                          ? [
                              {
                                fileName: field.value.fileName,
                                preview: field.value.previewUri ?? '',
                              },
                            ]
                          : []
                      }
                      onRemove={() => field.onChange(null)}
                    />
                    {fieldState.error && (
                      <FormHelperText error>
                        {fieldState.error.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </FieldRow>
          </>
        )
      )}

      {!viewOnly && (
        <ButtonGroup>
          <Button
            className={classes.marginLeftBtn}
            variant="outlined"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            className={classes.marginLeftBtn}
            variant="contained"
            color="primary"
            disabled={disabled}
            onClick={handleClickTranchePromotion}
          >
            {id ? 'Update' : 'Save'}
          </Button>
        </ButtonGroup>
      )}
    </RecordView>
  );
}
